import React, { useState } from "react";
import { motion } from "framer-motion";
import step1Image from '../../assets/images/atv.jpg';
import step3Image from '../../assets/images/step3.png';
import step4Image from '../../assets/images/robot2.jpeg';

const FadeIn = ({ children, className = '', delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    vineyard: "",
    acres: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setError(null);

    try {
      const response = await fetch('/api/schedule-demo.js', {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Server error' }));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      await response.json();
      setSubmitStatus("success");
      setFormData({
        name: "",
        email: "",
        phone: "",
        vineyard: "",
        acres: "",
        message: "",
      });
    } catch (error) {
      console.error("Error sending email:", error);
      setSubmitStatus("error");
      setError(error.message || "There was an error submitting your request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = [
    {
      image: step1Image,
      title: 'Quick Call',
      desc: '15-minute call to understand your vineyard and schedule the scan.',
      num: '01',
    },
    {
      image: step3Image,
      title: 'Free 5-Acre Scan',
      desc: 'We scan 5 acres of your vineyard and analyze every vine for disease.',
      num: '02',
    },
    {
      image: step4Image,
      title: 'Same-Day Data',
      desc: 'Get a detailed map on BudBase showing exactly which vines need attention.',
      num: '03',
    },
  ];

  const inputClasses = "w-full px-4 py-3 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-brand-500/40 focus:border-brand-500 dark:focus:border-brand-500 transition-all duration-200 text-sm";
  const labelClasses = "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5";

  return (
    <div className="min-h-screen bg-white dark:bg-[#0f1a09]">
      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-block px-4 py-1.5 mb-6 text-xs font-semibold tracking-[0.2em] uppercase text-brand-600 dark:text-brand-400 border border-brand-200 dark:border-brand-800/50 rounded-full bg-brand-50 dark:bg-brand-900/20"
          >
            Get Started
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-4xl md:text-6xl font-display text-gray-900 dark:text-white mb-6"
          >
            Get Your Free <span className="text-gradient">5-Acre Scan</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto"
          >
            See what your crew missed. We'll scan 5 acres of your vineyard and show you exactly which vines need attention.
          </motion.p>
        </div>
      </section>

      {/* Main content */}
      <section className="pb-24 md:pb-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Left - Steps */}
            <FadeIn>
              <div className="space-y-3">
                <h2 className="text-2xl font-display text-gray-900 dark:text-white mb-8">
                  What You'll Get
                </h2>
                {steps.map((step, i) => (
                  <div
                    key={step.num}
                    className="flex items-start gap-5 p-5 rounded-2xl bg-gray-50 dark:bg-white/[0.02] border border-gray-100 dark:border-white/5 card-lift"
                  >
                    <div className="flex-shrink-0 w-14 h-14 rounded-xl overflow-hidden">
                      <img src={step.image} alt={step.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold text-brand-500 dark:text-brand-400 tracking-wider">{step.num}</span>
                        <h3 className="font-semibold text-gray-900 dark:text-white text-base font-sans">{step.title}</h3>
                      </div>
                      <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Trust signals */}
              <div className="mt-10 p-6 rounded-2xl bg-brand-50 dark:bg-brand-900/10 border border-brand-100 dark:border-brand-900/30">
                <div className="flex items-center gap-3 mb-3">
                  <svg className="w-5 h-5 text-brand-600 dark:text-brand-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <span className="font-semibold text-gray-900 dark:text-white text-sm font-sans">No commitment required</span>
                </div>
                <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                  The 5-acre scan is completely free. We'll show you what we find and you decide if it's valuable. No pressure, no contracts.
                </p>
              </div>
            </FadeIn>

            {/* Right - Form */}
            <FadeIn delay={0.15}>
              <div className="p-8 md:p-10 rounded-2xl bg-white dark:bg-white/[0.02] border border-gray-200 dark:border-white/5 shadow-xl dark:shadow-none">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 font-sans">
                  Request Your Free Scan
                </h2>

                {submitStatus === "success" ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                  >
                    <div className="w-16 h-16 bg-brand-100 dark:bg-brand-900/30 rounded-full flex items-center justify-center mx-auto mb-5">
                      <svg className="w-8 h-8 text-brand-600 dark:text-brand-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 font-sans">
                      Scan Request Received!
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">
                      We'll contact you within 24 hours to schedule your free scan.
                    </p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="name" className={labelClasses}>Your Name</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className={inputClasses}
                          placeholder="John Smith"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className={labelClasses}>Email</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className={inputClasses}
                          placeholder="john@example.com"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="phone" className={labelClasses}>Phone</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className={inputClasses}
                          placeholder="(555) 123-4567"
                        />
                      </div>
                      <div>
                        <label htmlFor="acres" className={labelClasses}>Vineyard Size (acres)</label>
                        <input
                          type="number"
                          id="acres"
                          name="acres"
                          value={formData.acres}
                          onChange={handleChange}
                          required
                          className={inputClasses}
                          placeholder="100"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="vineyard" className={labelClasses}>Vineyard Name</label>
                      <input
                        type="text"
                        id="vineyard"
                        name="vineyard"
                        value={formData.vineyard}
                        onChange={handleChange}
                        required
                        className={inputClasses}
                        placeholder="Your Vineyard Name"
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className={labelClasses}>Additional Info (optional)</label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows="3"
                        className={inputClasses + " resize-none"}
                        placeholder="Tell us about your vineyard or any specific challenges..."
                      />
                    </div>
                    {submitStatus === "error" && (
                      <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/30">
                        <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
                      </div>
                    )}
                    <motion.button
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full py-3.5 bg-brand-500 hover:bg-brand-600 text-white font-semibold rounded-xl transition-all duration-200 text-sm shadow-lg shadow-brand-500/20 ${isSubmitting ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          Sending...
                        </span>
                      ) : (
                        "Schedule Your Free Scan"
                      )}
                    </motion.button>
                  </form>
                )}
              </div>
            </FadeIn>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
